<template>
  <div class="online-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-monitor"></i>
          在线管理
        </h1>
        <p class="page-description">监控用户登录状态，管理在线会话</p>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-section">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="8">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon online-icon">
                <i class="el-icon-user"></i>
              </div>
              <div class="stat-info">
                <div class="stat-value">{{ pagination.total }}</div>
                <div class="stat-label">在线会话数</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon user-icon">
                <i class="el-icon-s-custom"></i>
              </div>
              <div class="stat-info">
                <div class="stat-value">{{ uniqueUserCount }}</div>
                <div class="stat-label">在线用户数</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="8">
          <el-card class="stat-card" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon device-icon">
                <i class="el-icon-mobile-phone"></i>
              </div>
              <div class="stat-info">
                <div class="stat-value">{{ mobileCount }}</div>
                <div class="stat-label">移动设备</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <el-card class="search-card" shadow="never">
        <div class="search-form">
          <div class="search-row">
            <div class="search-item">
              <el-input
                v-model="searchForm.userId"
                placeholder="用户ID"
                prefix-icon="el-icon-key"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.username"
                placeholder="用户名（模糊查询）"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-actions">
              <el-button type="primary" icon="el-icon-search" @click="handleSearch">
                搜索
              </el-button>
              <el-button icon="el-icon-refresh" @click="handleReset">
                重置
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 操作区域 -->
    <div class="action-section">
      <el-card class="action-card" shadow="never">
        <div class="action-buttons">
          <el-button type="primary" icon="el-icon-refresh" @click="fetchOnlineUsers" :loading="loading">
            刷新列表
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-switch-button"
            :disabled="selectedSessions.length === 0"
            @click="handleBatchLogout"
          >
            批量下线 ({{ selectedSessions.length }})
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card class="table-card" shadow="never">
        <el-table
          :data="onlineUsers"
          v-loading="loading"
          @selection-change="handleSelectionChange"
          stripe
          class="online-table"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266', textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="userId" label="用户ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="120" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="username-cell">
                <el-avatar :size="32" class="user-avatar">
                  {{ scope.row.username.charAt(0).toUpperCase() }}
                </el-avatar>
                <span class="username-text">{{ scope.row.username }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="email" label="邮箱" min-width="180" align="center">
            <template slot-scope="scope">
              <div class="email-cell">
                <i class="el-icon-message"></i>
                <span>{{ scope.row.email || '-' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="ip" label="IP地址" width="140" align="center">
            <template slot-scope="scope">
              <el-tag size="small" type="info">{{ scope.row.ip }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="location" label="登录地点" width="120" align="center">
            <template slot-scope="scope">
              <div class="location-cell">
                <i class="el-icon-location-outline"></i>
                <span>{{ scope.row.location || '未知' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="deviceType" label="设备类型" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getDeviceTagType(scope.row.deviceType)" size="small">
                {{ getDeviceTypeLabel(scope.row.deviceType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="deviceName" label="设备名称" min-width="140" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="device-cell">
                <i :class="getDeviceIcon(scope.row.deviceType)"></i>
                <span>{{ scope.row.deviceName }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button
                type="text"
                icon="el-icon-switch-button"
                @click="handleLogout(scope.row)"
                class="logout-btn"
              >
                下线
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            class="pagination"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { getOnlineUsers, forceLogout, batchForceLogout } from '@/api/online'

export default {
  name: 'AdminOnline',
  data() {
    return {
      loading: false,
      onlineUsers: [],
      selectedSessions: [],
      searchForm: {
        userId: '',
        username: ''
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    uniqueUserCount() {
      const userIds = new Set(this.onlineUsers.map(u => u.userId))
      return userIds.size
    },
    mobileCount() {
      return this.onlineUsers.filter(u => u.deviceType === 'mobile' || u.deviceType === 'tablet').length
    }
  },
  created() {
    this.fetchOnlineUsers()
  },
  methods: {
    async fetchOnlineUsers() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          userId: this.searchForm.userId,
          username: this.searchForm.username
        }
        const response = await getOnlineUsers(params)
        this.onlineUsers = response.data.list || []
        this.pagination.total = response.data.total || 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchOnlineUsers()
    },

    handleReset() {
      this.searchForm = {
        userId: '',
        username: ''
      }
      this.pagination.page = 1
      this.fetchOnlineUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchOnlineUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchOnlineUsers()
    },

    handleSelectionChange(selection) {
      this.selectedSessions = selection
    },

    getDeviceTagType(deviceType) {
      const types = {
        'mobile': 'success',
        'tablet': 'warning',
        'desktop': ''
      }
      return types[deviceType] || 'info'
    },

    getDeviceTypeLabel(deviceType) {
      const labels = {
        'mobile': '移动端',
        'tablet': '平板',
        'desktop': '桌面端'
      }
      return labels[deviceType] || '未知'
    },

    getDeviceIcon(deviceType) {
      const icons = {
        'mobile': 'el-icon-mobile-phone',
        'tablet': 'el-icon-mobile',
        'desktop': 'el-icon-monitor'
      }
      return icons[deviceType] || 'el-icon-question'
    },

    handleLogout(row) {
      this.$confirm(`确定要将用户 "${row.username}" 的此会话下线吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await forceLogout({
          sessionId: row.sessionId,
          userId: row.userId
        })
        this.$message.success('用户已下线')
        this.fetchOnlineUsers()
      })
    },

    handleBatchLogout() {
      if (this.selectedSessions.length === 0) {
        this.$message.warning('请选择要下线的会话')
        return
      }

      this.$confirm(`确定要将选中的 ${this.selectedSessions.length} 个会话下线吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const sessions = this.selectedSessions.map(s => ({
          sessionId: s.sessionId,
          userId: s.userId
        }))
        await batchForceLogout({ sessions })
        this.$message.success('批量下线完成')
        this.fetchOnlineUsers()
      })
    }
  }
}
</script>

<style scoped>
.online-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 20px;
}

.header-content {
  background: linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%);
  padding: 30px;
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 20px rgba(54, 209, 220, 0.3);
}

.page-title {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-description {
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
}

/* 统计卡片 */
.stats-section {
  margin-bottom: 20px;
}

.stat-card {
  border-radius: 12px;
  border: none;
  transition: transform 0.3s;
}

.stat-card:hover {
  transform: translateY(-4px);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
}

.online-icon {
  background: linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%);
}

.user-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.device-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 4px;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.search-card {
  border-radius: 12px;
  border: none;
}

.search-form {
  padding: 10px 0;
}

.search-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-item {
  flex: 1;
  min-width: 180px;
  max-width: 250px;
}

.search-input {
  width: 100%;
}

.search-actions {
  display: flex;
  gap: 12px;
}

/* 操作区域 */
.action-section {
  margin-bottom: 20px;
}

.action-card {
  border-radius: 12px;
  border: none;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* 表格区域 */
.table-section {
  margin-bottom: 20px;
}

.table-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
}

.online-table {
  width: 100%;
}

.username-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.user-avatar {
  background: linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%);
  color: white;
  font-weight: 600;
  flex-shrink: 0;
}

.username-text {
  font-weight: 500;
  color: #303133;
}

.email-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #606266;
}

.location-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #606266;
}

.device-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #606266;
}

.logout-btn {
  color: #f56c6c;
}

.logout-btn:hover {
  color: #c45656;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
}

.pagination {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 响应式 */
@media (max-width: 768px) {
  .online-container {
    padding: 10px;
  }

  .page-title {
    font-size: 24px;
  }

  .stat-content {
    padding: 5px;
  }

  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }

  .stat-value {
    font-size: 24px;
  }
}
</style>
